import { useEffect, useState } from "react";
import { notifications } from "@mantine/notifications";
import {
  Button,
  Checkbox,
  Group,
  Loader,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useLanguage } from "../../../../../../stores/LanguageStore";
import {
  IconCheck,
  IconClock,
  IconClockCheck,
  IconCloudUpload,
  IconPhotoSensor2,
  IconSwitchHorizontal,
  IconX,
} from "@tabler/icons-react";
import ItemDeleter from "../../../../../../components/TableComponents/ItemDeleter";
import { parseDate } from "../../../../../../functions/dates";
import {
  deleteCron,
  executeCron,
  findCron,
  testCron,
} from "../../../../../../api/ConfigsApi";
import EditMake from "./EditMake";
import MakeActivator from "./MakeActivator";
import Jobs from "../../../../lists/components/jobs/Jobs";

const MakeItem = ({ item, handleReload, type }) => {
  const lang = useLanguage((s) => s.language);
  const [loading, setLoading] = useState(null);
  const [itemProfile, setItemProfile] = useState();
  const [finding, setFinding] = useState(true);

  const handleTest = () => {
    setLoading("test");
    testCron({ id: item?.id })
      .then(() => {
        notifications.show({
          message: lang.sync_zapier.test_success,
          color: "tertiary.8",
        });

        setFinding(true);
      })
      .finally(() => setLoading(false));
  };

  const handleSend = () => {
    setLoading("send");
    executeCron({ id: item?.id })
      .then(() => {
        notifications.show({
          message: lang.sync_zapier.send_success,
          color: "tertiary.8",
        });
        setFinding(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (finding) {
      findCron(item.id)
        .then((response) => {
          setItemProfile(response.data.cron_profile);
        })
        .finally(() => setFinding(false));
    }
  }, [finding, item.id]);  

  return (
    <Paper component={Stack} withBorder p={"sm"} pos={"relative"} w={"32%"}>
      <LoadingOverlay visible={loading} />
      <Group wrap="nowrap">
        <ThemeIcon color="black">
          <IconCloudUpload />
        </ThemeIcon>
        <Text size="lg">{item.name}</Text>

        <MakeActivator item={item} setLoading={setLoading} />
      </Group>

      <Text>
        {lang.sync_zapier.event_label} :{" "}
        {lang.sync_zapier.frequency[item.frequency]}
      </Text>

      <Group>
        <Group mr={8} miw={150}>
          <Tooltip label={lang.sync_zapier.last_run}>
            <IconClock size={18} />
          </Tooltip>
          <Text size="sm">{item?.ran_at ? parseDate(item?.ran_at) : "-"}</Text>
        </Group>

        <Group mr={8}>
          <Tooltip label={lang.contact_lists.cron.execution_time}>
            <IconClockCheck size={18} />
          </Tooltip>
          <Text size="sm">
            {item?.execution_time_seconds
              ? `${item?.execution_time_seconds}s`
              : "-"}
          </Text>
        </Group>
      </Group>

      {itemProfile && (
        <Group>
          <Text>{lang.sync_zapier.ignore} :</Text>
          <ThemeIcon
            color={
              itemProfile?.arguments?.resend_synchronized_contacts
                ? "red"
                : "green"
            }
          >
            {itemProfile?.arguments?.resend_synchronized_contacts ? (
              <IconX />
            ) : (
              <IconCheck />
            )}
          </ThemeIcon>
        </Group>
      )}

      {itemProfile?.state_details?.length > 0 && (
        <Group>
          <Text>{lang.contact_lists.cron.processes}</Text>
          <Jobs
            isZapier
            row={itemProfile}
            onDiff={() => {}}
            handleZapierLoad={() => setFinding(true)}
          />
          {finding && <Loader size={16} />}
        </Group>
      )}

      <Group>
        <Button
          loading={loading === "send"}
          variant="filled"
          loadingPosition="center"
          leftSection={<IconSwitchHorizontal size={18} />}
          onClick={handleSend}
        >
          {lang.sync_zapier.send}
        </Button>
        <Button
          loading={loading === "test"}
          variant="light"
          loadingPosition="center"
          leftSection={<IconPhotoSensor2 size={18} />}
          onClick={handleTest}
        >
          {lang.sync_zapier.test}
        </Button>

        {itemProfile && (
          <EditMake itemProfile={itemProfile} handleDone={() => setFinding(true)} />
        )}

        <ItemDeleter
          deletedMessage={lang.sync_zapier.delete_success}
          title_bold={`${item.name} #${item.id}`}
          handleDelete={deleteCron}
          id={item.id}
          onFinish={() => handleReload()}
        />
      </Group>
    </Paper>
  );
};

export default MakeItem;
